import { useTranslation } from 'react-i18next'
import { AttributeRichText } from '@/shared/components/ui/attribute-rich-text'
import { Checkbox } from '@/shared/components/ui/checkbox'
import { Label } from '@/shared/components/ui/label'
import { cn } from '@/shared/lib/utils'
import {
  type RegistrationConsents,
  registrationConsentEntries,
} from '@/shared/components/consumer-consent-kinds'

/**
 * Registration consent checkboxes (T&C, marketing email) for the Sage wizard.
 * Wording comes from the tenant's config; boxes are never pre-ticked. A
 * required box left unticked is highlighted once `invalid` lists it.
 */
export function ConsumerConsentFields({
  config,
  values,
  onChange,
  invalid = [],
}: {
  config?: RegistrationConsents | null
  values: Record<string, boolean>
  onChange: (kind: string, checked: boolean) => void
  invalid?: string[]
}) {
  const { t } = useTranslation()
  const entries = registrationConsentEntries(config)
  if (entries.length === 0) return null

  return (
    <div className="flex flex-col gap-3" data-testid="consumer-consent-fields">
      {entries.map(([kind, cfg]) => {
        const isInvalid = invalid.includes(kind)
        return (
          <div
            key={kind}
            className={cn(
              'rounded-[6px] border-[0.5px] border-[var(--rule)] px-4 py-3',
              isInvalid && 'ring-1 ring-[var(--status-error)]',
            )}
            data-testid={`consumer-consent-${kind}`}
          >
            <div className="flex items-start gap-3">
              <Checkbox
                id={`consent-${kind}`}
                checked={Boolean(values[kind])}
                onCheckedChange={(checked) => onChange(kind, checked === true)}
                aria-invalid={isInvalid}
                data-testid={`consumer-consent-${kind}-checkbox`}
              />
              <div className="min-w-0">
                <Label
                  htmlFor={`consent-${kind}`}
                  className={cn(
                    'text-[13px] text-[var(--ink)]',
                    isInvalid && 'text-[var(--status-error)]',
                  )}
                >
                  <AttributeRichText content={cfg.label} variant="inline" />
                </Label>
                {cfg.purpose ? (
                  <p className="mt-1 text-[12px] text-[var(--ink-3)]">{cfg.purpose}</p>
                ) : null}
                {isInvalid ? (
                  <p className="mt-1 text-[11px] text-[var(--status-error)]">
                    {t('consent.termsRequired', 'Please accept the terms and conditions.')}
                  </p>
                ) : null}
              </div>
            </div>
          </div>
        )
      })}
    </div>
  )
}
